import React from 'react';
import { Coins, Wrench, Scissors, Hand, ChevronRight, AlertCircle } from 'lucide-react';
import { formatMoney } from '../../utils/format';

/**
 * BoardResolutionsGrid — 4-resolution voting grid surfaced by the annual
 * board meeting. Extracted from `AnnualBoardMeeting.jsx` (04/06/2026
 * refactor). Pure presentation: receives the computed `resolutions` array
 * + the currently selected id + a setter. Zero behaviour change.
 */
export default function BoardResolutionsGrid({ resolutions, chosen, onChoose }) {
  return (
    <div className="border-t border-ink-600 pt-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-1.5 rounded bg-emerald-900/30">
          <Hand className="w-4 h-4 text-emerald-400" />
        </div>
        <div>
          <h4 className="font-bold text-sm text-slate-200">Vote du conseil</h4>
          <p className="text-[10px] text-slate-500">Choisissez une résolution pour l&apos;exercice suivant</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="board-resolutions">
        {resolutions.map(R => (
          <ResolutionCard key={R.id} resolution={R} selected={chosen === R.id} onChoose={onChoose} />
        ))}
      </div>
    </div>
  );
}

function ResolutionCard({ resolution: R, selected, onChoose }) {
  const accentMap = {
    emerald: 'border-emerald-500 bg-emerald-500/10',
    sky:     'border-sky-500 bg-sky-500/10',
    amber:   'border-amber-500 bg-amber-500/10',
    slate:   'border-slate-500 bg-slate-500/10',
  };
  const Icon = R.icon;
  return (
    <button
      type="button"
      onClick={() => R.enabled && onChoose(R.id)}
      disabled={!R.enabled}
      className={`text-left rounded-lg border-2 p-3 transition-all ${
        !R.enabled
          ? 'border-ink-600 bg-ink-700/20 opacity-50 cursor-not-allowed'
          : selected
            ? accentMap[R.accent]
            : 'border-ink-600 bg-ink-700/30 hover:border-ink-500'
      }`}
      data-testid={`resolution-${R.id}`}
    >
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selected ? `text-${R.accent}-400` : 'text-slate-400'}`} />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-xs text-slate-100">{R.title}</p>
          <p className="text-[10px] text-slate-500 mt-0.5">{R.sub}</p>
          {R.cost > 0 && (
            <p className="text-[10px] text-danger mt-1 font-mono">Coût : −{formatMoney(R.cost)}</p>
          )}
          <ul className="text-[10px] text-slate-400 mt-1 space-y-0.5">
            {R.bullets.map((b) => (
              <li key={`${R.id}-${b}`} className="flex items-start gap-1">
                <ChevronRight className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          {!R.enabled && R.disabledHint && (
            <p className="text-[10px] text-danger mt-1 flex items-center gap-1">
              <AlertCircle className="w-2.5 h-2.5" />
              {R.disabledHint}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

/* ─── Pure helper : build the resolutions data ────────────────────────── */
/**
 * Build the 4 voting options based on the annual report + current treasury
 * and driver count. Pure (deterministic) so tests can lock the rules.
 *
 * 22/07/2026 (retour user) :
 *   • Dividende → montant LIBRE (curseurs prime équipe / dividende perso)
 *     au lieu du forfait 30 % jugé inabordable.
 *   • Plan d'investissement → 8 % du résultat (plafonné 250 k€) au lieu de
 *     20 %, avec projection d'économies affichée pour juger la rentabilité.
 */
export function buildResolutions(report, treasury, driversCount, opts = {}) {
  const net = Math.max(0, Math.round(report.netResult || 0));
  const maxDividend = Math.min(net, Math.max(0, Math.round((treasury || 0) - 5000)));
  const investCost = Math.min(250000, Math.max(0, Math.round(net * 0.08)));
  const maintenanceMonthly = opts.maintenanceMonthly || 0;
  const fuelMonthly = opts.fuelMonthly || 0;
  const projectedSavings = Math.round(maintenanceMonthly * 12 * 0.10 + fuelMonthly * 12 * 0.05);
  const investBullets = [
    'Trésorerie −8 % du résultat (plafonné 250 k€)',
    '−10 % entretien & −5 % carburant pendant 12 mois',
  ];
  if (projectedSavings > 0) {
    investBullets.push(
      `Économies projetées ≈ ${formatMoney(projectedSavings)} sur 12 mois ${
        projectedSavings >= investCost ? '→ rentable' : '→ non rentable à ce niveau de flotte'
      }`,
    );
  }
  return [
    {
      id: 'dividend',
      icon: Coins,
      title: 'Verser un dividende',
      sub: 'Montant libre — prime équipe et/ou dividende personnel',
      cost: 0,
      maxDividend,
      bullets: [
        'Réglez la répartition avec les curseurs ci-dessous',
        'Prime collaborateurs → +satisfaction équipe',
        'Dividende personnel → patrimoine dirigeant',
        '+5 réputation/notoriété si ≥ 10 % du résultat (+2 sinon)',
      ],
      accent: 'emerald',
      enabled: net > 0 && maxDividend >= 500,
      disabledHint: net <= 0 ? 'Pas de bénéfice à distribuer' : 'Trésorerie insuffisante',
    },
    {
      id: 'investment',
      icon: Wrench,
      title: "Plan d'investissement",
      sub: 'Modernisation flotte sur 12 mois',
      cost: investCost,
      bullets: investBullets,
      accent: 'sky',
      enabled: report.netResult > 0 && treasury >= investCost,
      disabledHint: report.netResult <= 0 ? 'Pas de bénéfice à investir' : 'Trésorerie insuffisante',
    },
    {
      id: 'restructuring',
      icon: Scissors,
      title: 'Plan de restructuration',
      sub: 'Cost-cutting agressif sur 12 mois',
      cost: 0,
      bullets: ['−8 % sur les salaires chauffeurs pendant 12 mois', `Satisfaction de l'équipe −5 (immédiat, ${driversCount} chauffeur·s)`],
      accent: 'amber',
      enabled: true,
    },
    {
      id: 'statu_quo',
      icon: Hand,
      title: 'Statu quo',
      sub: 'Aucune résolution votée',
      cost: 0,
      bullets: ["Pas d'effet sur l'exercice suivant"],
      accent: 'slate',
      enabled: true,
    },
  ];
}
